"use client";

import { Bot } from "lucide-react";
import type { ComponentProps } from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/** What the model control announces: the model, and its intelligence unless
 *  a Venus model (which has no reasoning setting) is selected. */
export function promptModelTriggerLabel(props: {
  venus: boolean;
  modelLabel: string;
  reasoningLabel: string;
}) {
  return props.venus
    ? `Model: ${props.modelLabel}`
    : `Model: ${props.modelLabel}; Intelligence: ${props.reasoningLabel}`;
}

/**
 * The model control's button. Ghost at the defaults, secondary once a model
 * or intelligence level is chosen. PromptModelControls opens its popover from
 * it; the button forwards whatever the popover and tooltip triggers add.
 */
export function PromptModelTriggerButton({
  explicit,
  className,
  ...props
}: Omit<ComponentProps<typeof Button>, "type" | "size" | "variant"> & {
  /** A non-default model or intelligence level is selected. */
  explicit: boolean;
}) {
  return (
    <Button
      type="button"
      size="icon"
      variant={explicit ? "secondary" : "ghost"}
      className={cn(
        "size-8 rounded-md",
        !explicit && "text-muted-foreground hover:text-foreground",
        className,
      )}
      {...props}
    />
  );
}

/** The closed model control as it sits in the composer footer. */
export function PromptModelTriggerView(props: {
  explicit: boolean;
  label: string;
  modelId: string;
  reasoning: string;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      <PromptModelTriggerButton
        explicit={props.explicit}
        disabled={props.disabled}
        aria-label={props.label}
        data-selected-model={props.modelId}
        data-selected-reasoning={props.reasoning}
      >
        <Bot className="size-3.5" aria-hidden />
      </PromptModelTriggerButton>
    </div>
  );
}
